import {Component, OnDestroy, OnInit} from '@angular/core';
import {ButtonTypeEnum} from '@shared/button/button.directive';
import {BreadCrumbInterface} from '@shared/interfaces/breadcrumb.interface';
import {BreadcrumbActions} from '@store/breadcrumb';
import {AppTranslateService} from '@core/services/translate/translate.service';
import {Store} from '@ngrx/store';
import {AppRouteEnum} from '@/router/router.module';
import {HeaderTitleService} from '@component/header-title/header-title.service';
import {MatDialog} from '@angular/material/dialog';
import {AppExamAssignDialogComponent} from '@component/exam/assign-dialog/assign-dialog.component';
import {GroupActions} from '@store/group';
import {OperatorFunction, Subscription} from 'rxjs';
import {AppExamSelectors} from '@component/exam/redux/exam.selectors';
import {ApiExamListEntryInterface, ApiExamListInterface, ApiExamPlanInterface} from '@component/exam/exam.service';
import {AppExamActions} from '@component/exam/redux/exam.actions';
import {filter} from 'rxjs/operators';
import * as moment from 'moment';
import {ExamEvent} from '@component/exam/card-exam/card-exam.component';
import {ExamDeleteDialogComponent} from '@component/exam/exam-delete-dialog/exam-delete-dialog.component';
import {ExamTypeEnum} from '@component/exam/card-exam/exam.type.enum';

export interface ExamCountsInterface {
    total: number;
    active: number;
    finished: number;
    upcoming: number;
}

@Component({
    selector: 'app-exam',
    templateUrl: './exam.component.html',
    styleUrls: ['./exam.component.scss'],
})
export class AppExamComponent implements OnInit, OnDestroy {
    readonly #examTypes: number[] = [2];

    readonly #subscriptions: Subscription[] = [];

    readonly buttonType = ButtonTypeEnum;

    #store: Store;

    #translator: AppTranslateService;

    currentWeekNumber = moment().isoWeek();

    currentYear = moment().year();

    previousExamListEntry?: ApiExamListEntryInterface;

    currentExamListEntry?: ApiExamListEntryInterface;

    nextExamListEntry?: ApiExamListEntryInterface;

    isLoading: boolean = true;

    constructor(
        translator: AppTranslateService,
        store: Store,
        private headerTitleService: HeaderTitleService,
        private translateService: AppTranslateService,
        private dialog: MatDialog,
    ) {
        this.#translator = translator;
        this.#store = store;
    }

    ngOnInit(): void {
        this.#subscriptions.push(
            this.#store.select(AppExamSelectors.selectList)
                .pipe(filter((list?: ApiExamListInterface) => undefined !== list) as OperatorFunction<ApiExamListInterface | undefined, ApiExamListInterface>)
                .subscribe((list: ApiExamListInterface) => this.#handleSelectList(list)),
        );

        const placeHolder = this.translateService.instant('exam.placeholder');

        this.headerTitleService.setHeaderTitle(placeHolder);

        this.#loadBreadCrumbs();
        this.#store.dispatch(GroupActions.fetchGroups({}));
        this.#store.dispatch(AppExamActions.fetchAssigned({types: this.#examTypes})); // For now, we only want the "real" exams.
    }

    ngOnDestroy(): void {
        this.#subscriptions.forEach((subscription: Subscription) => subscription.unsubscribe());
    }

    #loadBreadCrumbs(): void {
        const breadcrumbs: BreadCrumbInterface[] = [
            {label: this.#translator.instant('exam.breadcrumb'), url: '/' + AppRouteEnum.Exam},
        ];

        this.#store.dispatch(BreadcrumbActions.setBreadcrumbsAction({breadcrumbs}));
    }

    examAssign(examPlan?: ExamEvent): void {
        switch (examPlan?.type) {
            case ExamTypeEnum.edit:
                this.#addEditExam(examPlan.data);
                break;
            case ExamTypeEnum.delete:
                this.#deleteExam(examPlan.data);
                break;
            case undefined:
                this.#addEditExam();
        }
    }

    #addEditExam(examPlan?: ApiExamPlanInterface): void {
        this.dialog.open(AppExamAssignDialogComponent, {panelClass: `custom-dialog-container`, data: {examPlan}, maxHeight: '90vh'})
            .afterClosed()
            .pipe(filter((result?: boolean) => true === result)).subscribe(() => this.#store.dispatch(AppExamActions.fetchAssigned({types: [2]})));
    }

    #deleteExam(examPlan: ApiExamPlanInterface) : void {
        this.dialog.open(ExamDeleteDialogComponent, {panelClass: `custom-dialog-container`, data: examPlan})
            .afterClosed()
            .pipe(filter((result?: boolean) => true === result)).subscribe(() => this.#store.dispatch(AppExamActions.fetchAssigned({types: [2]})));
    }

    #handleSelectList(list: ApiExamListInterface): void {
        this.previousExamListEntry = list.previous;
        this.currentExamListEntry = list.current;
        this.nextExamListEntry = list.next;
    }

    handleExamNavigation(direction: number): void {
        const previousExamListEntry: ApiExamListEntryInterface | undefined = this.previousExamListEntry;
        const nextExamListEntry: ApiExamListEntryInterface | undefined = this.nextExamListEntry;

        if (undefined === previousExamListEntry) {
            throw new Error('Previous exam list entry is undefined');
        }

        if (undefined === nextExamListEntry) {
            throw new Error('Next exam list entry is undefined');
        }

        const offset: number = -1 === direction ? previousExamListEntry.offset : nextExamListEntry.offset;
        const year: number = -1 === direction ? previousExamListEntry.year : nextExamListEntry.year;

        this.#store.dispatch(AppExamActions.fetchAssigned({types: this.#examTypes, offset, year}));
    }
}
